import { DisabledPrompt, useBCMSContext, BCMSStateManagerCRUD, useStateManager, BCMSRecord, StateManager, BCMSDefaultModel } from "bcms-react";
import Classes, { ParticipantImportTemplate } from "model";
import { FormContainer, FormRowView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView, WidgetContainer, WidgetView } from 'components/shared';
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton, UICheckbox, UITextInput } from "components/ui";
import React from 'react';
import { SVGAsset } from 'res';
import "./mobile.css";
import "./styles.css";
/**
 * @function ParticipantImportTemplatesWidget
 * @return {React.Component}
 * @exported true
 * @description The widget that shows the participant import templates
 */

export default function ParticipantImportTemplatesWidget(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const {updateImportTemplates} = props.actions ?? {};

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,
    // Toggles the template form. "show" is a boolean value of `true` or `false`.
    showTemplateForm: show => () => {
      BCMSStateManagerCRUD.init(state)
        .update(["ui", "showAddTemplateFormView"], show)
        .update(["data", "template"], ParticipantImportTemplate.init({}))
        .submit(setState);
        window.scrollTo(0,0);
    },
    showMatchCriteria: show => () => {
      BCMSStateManagerCRUD.init(state)
        .update(["ui", "showMatchCriteria"], show)
        .submit(setState);
    },
    onChangeForm: prop => value => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "template", prop], value)
        .submit(setState);
    },
    onSave: ()=>{
      const defaultTemplate = BCMSDefaultModel.participantImportTemplate();
      const importTemplate = {
        ...state.data.template.record,
        id: ParticipantImportTemplate.nextID()
      }
      const participantImportTemplate = ParticipantImportTemplate.init(importTemplate);
      const mergeRecord = BCMSDefaultModel.mergeRecords(participantImportTemplate, defaultTemplate);

      BCMSRecord.batch([
        ParticipantImportTemplate.create({ ...mergeRecord })
      ], globalController, Classes);

      BCMSStateManagerCRUD.init(state)
        .update(["ui", "showAddTemplateFormView"], false)
        .submit(setState);

        updateImportTemplates(ParticipantImportTemplate.initAll());
        window.scrollTo(0,0);
    }
  });

  return (
    <StateManager className="stop-payment-page" state={{ data, ui }} controller={controller}>
      <WidgetContent />
    </StateManager>
  );
}

export const getDefaultData = () => ({
  // empty for now.
});
export const getDefaultUI = () => ({
  showAddTemplateFormView: false,
  showMatchCriteria: true
});

function WidgetContent(props) {
  const { data, ui, showTemplateForm } = useStateManager();
  const templates = data.importTemplates ?? [];
  const templateItems = templates.map((item, key) => <ParticipantImportTableRow key={key} data={item} />, []);
  const templateRows = templateItems.length > 0 ? templateItems : <NoFileFormatTableRow />;

  return (
    <WidgetContainer className="participants-import-templates-widget">
      <WidgetView dataPoint="Participant_Import_Templates_Label" text="Participant Import Templates">
        {!ui.showAddTemplateFormView &&
          <>
            <TableView className="participants-import-templates-table">
              <TableColumnContainer>
                <TableColumnView className="text-left" text={<>Template Name<DisabledPrompt><SVGAsset id="ArrowUpIcon" className="clickable" /></DisabledPrompt></>} />
                <TableColumnView className="text-left" text={<>Last Updated Date<DisabledPrompt><SVGAsset id="SortIcon" className="clickable" /></DisabledPrompt></>} />
                <TableColumnView className="actions-column" />
              </TableColumnContainer>
              <TableBody>
                {templateRows}
              </TableBody>
            </TableView>
            <PrimaryButton dataPoint={ `Add_Template_Button` } text="Add Template" className="add-template" onClick={showTemplateForm(true)} />
          </>
        }
        {ui.showAddTemplateFormView && <AddTemplateFormView />}
      </WidgetView>
    </WidgetContainer>
  );
}


/**
 * @function NoFileFormatTableRow
 * @return {React.Component}
 * @exported false
 * @description The table row that shows when no file format is available
 */

function NoFileFormatTableRow(props) {
  return (
    <TableItemContainer className="no-file-format-table-row">
      <td className="text-center table-item-view" colSpan="3">No file formats defined yet.</td>
    </TableItemContainer>
  );
}

/**
 * @function ParticipantImportTableRow
 * @return {React.Component}
 * @exported false
 * @description The table row for the 
 */

function ParticipantImportTableRow(props) {
  const data = props.data;

  return (
    <TableItemContainer>
      <TableItemTextView dataPoint={ `Template_Name_${ data.id }`} text={data.templateName} />
      <TableItemTextView text={`${data.updatedDate.format("m/d/Y")} ${data.updatedDate.format("h:i p")}`} />
      <TableItemView className="actions-column">
        <div className="flex-container flex-horizontal align-center">
          <DisabledPrompt>
            <SVGAsset data-point={ `Template_Edit_Icon_${ data.id }`} id="EditIcon" className="edit-icon clickable" />
          </DisabledPrompt>
          <DisabledPrompt>
            <SVGAsset data-point={ `Template_Delete_Icon_${ data.id }`} id="TrashcanXIcon" className="trash-icon clickable" />
          </DisabledPrompt>
        </div>
      </TableItemView>
    </TableItemContainer>
  );
}


/**
 * @function AddTemplateFormView
 * @return {React.Component}
 * @exported false
 * @description The form that shows when adding a new template is created
 */

function AddTemplateFormView(props) {
  const { ui, data, showMatchCriteria, onChangeForm, showTemplateForm, onSave } = useStateManager();
  const template = data.template;
  const matchCriteria = ui.showMatchCriteria;

  return (
    <FormContainer className="add-template-form-view small-form-width">
      <FormRowView text="* Template Name">
        <UITextInput dataPoint={ `Template_Name_Input` } value={template.templateName} onChange={onChangeForm("templateName")} />
      </FormRowView>
      <FormRowView text="" className="checkbox-form-row">
        <UICheckbox dataPoint={ `Update_Existing_Participants` } label="Update Existing Participants" value="checked" onChange={showMatchCriteria(!matchCriteria)} />
      </FormRowView>
      {matchCriteria &&
        <>
          <p className="form-heading">Match Criteria</p>
          <FormRowView text="" className="checkbox-form-row">
            <UICheckbox dataPoint={ `Match_Criteria_Name` } label="Name" value="checked" />
          </FormRowView>
          <FormRowView text="" className="checkbox-form-row">
            <UICheckbox dataPoint={ `Match_Criteria_Nickname` } label="Nickname" value="checked" />
          </FormRowView>
          <FormRowView text="" className="checkbox-form-row">
            <UICheckbox dataPoint={ `Match_Criteria_Unique_Identifier` } label="Unique Identifier" value="checked" />
          </FormRowView>
          <FormRowView text="" className="checkbox-form-row">
            <UICheckbox dataPoint={ `Match_Criteria_Account_Number` } label="Account Number" value="checked" />
          </FormRowView>
          <b className="form-heading">If Account Number is selected, Routing Number and Account Type will also be included as match criteria.</b>

        </>
      }
      <p className="form-heading field-order">Field Order</p>
      <FormRowView text="* Name" className="name-form-row">
        <UITextInput dataPoint={ `Field_Order_Name_Input` } value={template.participantName} onChange={onChangeForm("participantName")} />
      </FormRowView>
      <FormRowView text="Nickname" className="nickname-form-row">
        <UITextInput dataPoint={ `Field_Order_Nickname_Input` } value={template.nickname} onChange={onChangeForm("nickname")} />
      </FormRowView>
      <FormRowView text="Institution Name">
        <UITextInput value={template.institutionName} onChange={onChangeForm("institutionName")} />
      </FormRowView>
      <FormRowView text="* Routing Number" className="account-number-form-row">
        <UITextInput value={template.routing} onChange={onChangeForm("routing")} />
      </FormRowView>
      <FormRowView text="* Account Number" className="account-number-form-row">
        <UITextInput value={template.number} onChange={onChangeForm("number")} />
      </FormRowView>
      <FormRowView text="* Account Type" className="account-number-form-row">
        <UITextInput value={template.accountType} onChange={onChangeForm("accountType")} />
      </FormRowView>
      <FormRowView text="* Unique Identifier" className="unique-id-form-row">
        <UITextInput value={template.uniqueID} onChange={onChangeForm("uniqueID")} />
      </FormRowView>
      <FormRowView text="* Status">
        <UITextInput value={template.status} onChange={onChangeForm("status")} />
      </FormRowView>
      <FormRowView text="Group">
        <UITextInput value={template.group} onChange={onChangeForm("group")} />
      </FormRowView>
      <FormRowView text="Discretionary Data">
        <UITextInput dataPoint={ `Field_Order_Discretionary_Data_Input` } value={template.discretionaryData} onChange={onChangeForm("discretionaryData")} />
      </FormRowView>
      <FormButtonRow>
        <SecondaryButton text="Cancel" onClick={showTemplateForm(false)} />
      <PrimaryButton dataPoint={ `Save_Button` } text="Save" onClick={onSave} />
      </FormButtonRow>
    </FormContainer>
  );
}